import { ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, delay, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { combineEpicsIsolated } from 'utils/epicRegistry'
import { extractErrorMessage } from 'utils/ErrorMessages'
import LocalStorage from 'utils/LocalStorage'
import { checkPerm } from 'utils/permManager'
import { URL_CONST } from 'utils/urlConsts'
import { matchURLStartsWith } from 'utils/urls'

import { FETCH_APP_DATA_ASYNC } from 'containers/App/constants'
import { selectActualPermissions } from 'containers/App/selectors'
import { CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA } from 'containers/AwarenessCenter/WizardCampaign/WizardSpearPhishingCampaign/constants'
import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { MINIMAL_ALLOWED_POLLING_INTERVAL } from 'containers/Polling/constants'
import { addToast } from 'containers/Toasts/actions'

import PopupMessage from 'components/lib/DesignSystem/PopupMessage'

import callForActionEpics from './CallForAction/epics'
import callForActionFormEpics from './CallForActionForm/epics'
import campaignsAutonomousSchedulerEpics from './Campaigns/AutonomousScheduler/epics'
import campaignReportEpics from './Campaigns/CampaignReport/epics'
import { CANCEL_CAMPAIGN_ASYNC } from './Campaigns/constants'
import campaignsEpics from './Campaigns/epics'
import landingPageFormEpics from './LandingPageForm/epics'
import landingPagesEpics from './LandingPages/epics'
import scenariosEpics from './Scenarios/epics'
import { APPROVE_CAMPAIGN_ASYNC } from './WizardCampaign/steps/constants'
import generateEmailsStepEpics from './WizardCampaign/steps/GenerateEmailsStep/epics'
import messageStepEpics from './WizardCampaign/steps/MessageStep/epics'
import reviewAndLaunchStepEpics from './WizardCampaign/steps/ReviewAndLaunchStep/epics'
import setupStepEpics from './WizardCampaign/steps/SetupStep/epics'
import trainingModuleStepEpics from './WizardCampaign/steps/TrainingModuleStep/epics'
import wizardCampaignEditEpics from './WizardCampaign/WizardCampaignEdit/epics'
import wizardSimulationCampaignEpics from './WizardCampaign/WizardSimulationCampaign/epics'
import wizardSpearPhishingCampaignEpics from './WizardCampaign/WizardSpearPhishingCampaign/epics'
import wizardTrainingCampaignEpics from './WizardCampaign/WizardTrainingCampaign/epics'
import {
  saveNotifyManagerDefaultsError,
  saveNotifyManagerDefaultsSuccess,
  spGenerationPolling,
  spGenerationPollingError,
  spGenerationPollingSuccess,
  submitFetchCampaignsSettings,
  submitFetchCampaignsSettingsError,
  submitFetchCampaignsSettingsSuccess,
  submitUpsellSeenIndicationError,
  submitUpsellSeenIndicationSuccess,
} from './actions'
import {
  FETCH_CAMPAIGNS_SETTINGS,
  SAVE_NOTIFY_MANAGER_DEFAULTS,
  SP_GENERATION_POLLING_ASYNC,
  SUBMIT_UPSELL_SEEN_INDICATION,
} from './constants'

const submitUpsellSeenIndicationEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SUBMIT_UPSELL_SEEN_INDICATION.INITIATE),
    mergeMap(() =>
      api.accounts.submitUpsellSeenIndication().pipe(
        map((response) => submitUpsellSeenIndicationSuccess(response)),
        catchError((error) => of(epicsErrorHandler(submitUpsellSeenIndicationError, error))),
        takeUntil(action$.pipe(ofType(SUBMIT_UPSELL_SEEN_INDICATION.CANCEL)))
      )
    )
  )

const fetchCampaignsSettingsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_CAMPAIGNS_SETTINGS.INITIATE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      APPROVE_CAMPAIGN_ASYNC.SUCCESS,
      CANCEL_CAMPAIGN_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS) {
        return matchURLStartsWith(URL_CONST.awarenessCenter.self)
      }
      return true
    }),
    mergeMap(() => {
      const companyFilter = selectCompanyFilter(state$.value)
      return api.awarenessCenter.getSettings(companyFilter?.value).pipe(
        mergeMap((response) => of(submitFetchCampaignsSettingsSuccess(response))),
        catchError((error) =>
          of(epicsErrorHandler(submitFetchCampaignsSettingsError, error, [403]))
        ),
        takeUntil(action$.pipe(ofType(FETCH_CAMPAIGNS_SETTINGS.CANCEL)))
      )
    })
  )

export const saveNotifyManagerDefaultsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_NOTIFY_MANAGER_DEFAULTS.INITIATE),
    mergeMap((action) => {
      const companyFilter = selectCompanyFilter(state$.value)
      return api.awarenessCenter
        .saveNotifyManagerDefaults(companyFilter?.value, action.payload)
        .pipe(
          mergeMap((response) =>
            of(saveNotifyManagerDefaultsSuccess(response), submitFetchCampaignsSettings())
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(saveNotifyManagerDefaultsError, error),
              addToast({
                ds: true,
                type: 'error',
                title: 'Error',
                message: extractErrorMessage(error),
              })
            )
          ),
          takeUntil(action$.pipe(ofType(SAVE_NOTIFY_MANAGER_DEFAULTS.CANCEL)))
        )
    })
  )

const spearPhishingGenerationPollingEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    // eslint-disable-next-line ironscales/fetch-selected-company-filter-guard -- known gap: on FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS this only checks edit_campaigns permission, not the page URL, unlike the sibling fetchCampaignsSettingsEpic above in this file. Not fixed here (IC-50897 is lint-rule-only); flagged as a possible real bug for follow-up.
    ofType(
      FETCH_APP_DATA_ASYNC.SUCCESS,
      SP_GENERATION_POLLING_ASYNC.INITIATE,
      SP_GENERATION_POLLING_ASYNC.SUCCESS,
      CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (
        [FETCH_APP_DATA_ASYNC.SUCCESS, FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS].includes(
          action.type
        )
      ) {
        return checkPerm(selectActualPermissions(state$.value), ['edit_campaigns'])
      } else if (action.type === CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA)
        return action.payload.notifyGenerationFinished
      else return true
    }),
    delay(MINIMAL_ALLOWED_POLLING_INTERVAL),
    switchMap(() => {
      if (document.visibilityState === 'hidden') {
        return of(spGenerationPolling())
      } else {
        return api.awarenessCenter
          .fetchSPGenerationStatus(state$.value.partnerOptions.selectedCompany?.value)
          .pipe(
            mergeMap((response) => {
              const actionsToReturn = []

              if (response?.finishedCampaignIds?.length) {
                let showMessage = true
                if (LocalStorage.isAvailable) {
                  const lsData = LocalStorage.getAwarenessCenter() || {}
                  const campaignIds = getCampaignIdsToShowMessage(
                    lsData,
                    response.finishedCampaignIds
                  )

                  // NOTE: if a user is in the wizard and got two finished campaigns
                  // both notifications will be lost. Can be fixed by tracking exact ids.

                  if (
                    !campaignIds.length ||
                    campaignIds.some((id) => window.location.href.includes(id))
                  ) {
                    showMessage = false
                  }

                  LocalStorage.setAwarenessCenter({
                    ...lsData,
                    spearPhishingGenerationPooling: {
                      ...lsData.spearPhishingGenerationPooling,
                      finishedCampaignIds: response.finishedCampaignIds,
                    },
                  })
                }

                if (showMessage) {
                  actionsToReturn.push(
                    addToast({
                      ds: true,
                      cover: PopupMessage.COVERS.TARGETED_EMAILS,
                      message:
                        "Your Spear Phishing Emails are all set! Just a few final touches and we're ready to launch the campaign!",
                      boldInMessage: 'Spear Phishing Emails',
                      title: 'Your emails are ready!',
                      ctaInternalRedirect: URL_CONST.awarenessCenter.campaigns,
                      ctaText: 'Yes, continue editing',
                      className: 'spear-phishing-emails-ready-message',
                    })
                  )
                }
              }

              if (response.inProgressCampaignIds.length) {
                actionsToReturn.push(spGenerationPollingSuccess()) // causes short polling
              }

              return of(...actionsToReturn)
            }),
            catchError((error) => of(epicsErrorHandler(spGenerationPollingError, error, [403])))
          )
      }
    })
  )

function getCampaignIdsToShowMessage(lsData, finishedCampaignIds) {
  if (lsData.spearPhishingGenerationPooling?.finishedCampaignIds) {
    const shownCampaigns = lsData.spearPhishingGenerationPooling.finishedCampaignIds
    const newCampaigns = finishedCampaignIds.filter((id) => !shownCampaigns.includes(id))

    return newCampaigns.length ? newCampaigns : []
  }

  return finishedCampaignIds
}

export default combineEpicsIsolated(
  fetchCampaignsSettingsEpic,
  saveNotifyManagerDefaultsEpic,
  submitUpsellSeenIndicationEpic,
  spearPhishingGenerationPollingEpic,
  campaignsEpics,
  scenariosEpics,
  callForActionEpics,
  callForActionFormEpics,
  landingPagesEpics,
  landingPageFormEpics,
  campaignsAutonomousSchedulerEpics,
  campaignReportEpics,
  setupStepEpics,
  trainingModuleStepEpics,
  reviewAndLaunchStepEpics,
  wizardSimulationCampaignEpics,
  wizardTrainingCampaignEpics,
  wizardSpearPhishingCampaignEpics,
  wizardCampaignEditEpics,
  messageStepEpics,
  generateEmailsStepEpics
)
